import {
	ConfigurationValue,
	Domain,
	RuleFactory,
	isProperty as coreIsProperty,
} from "@animech/axiom-format";
import { isArrayDomain, isRangeDomain } from "@animech/axiom-utility";
import * as Utility from "@animech/axiom-utility";
import {
	Configurator as CoreConfigurator,
	ConfiguratorChange as CoreConfiguratorChange,
	ConfiguratorOptions,
} from "@animech/axiom-configurator";
import { Localizer, MetaRegistry } from "@animech/axiom-ui-builder";
import {
	Localizer as BoundLocalizer,
	MetaRegistry as BoundMetaRegistry,
	PropertyMetaTree,
} from "../common/bindings";
import {
	Definitions,
	query,
	Root,
	RootProperty,
	RuleApiExtensions,
	UiProperty,
	Localization,
	Locale,
} from "src/definitions";
import { Property, PropertyValue } from "src/common";

export type Configurator = CoreConfigurator<Root, RuleApiExtensions>;
export type ConfiguratorChange = CoreConfiguratorChange<Root>;

export type PropertyUpdate = PropertyValue;

export function makeConfigurator(
	root: RootProperty,
	ruleFactory: RuleFactory<Root, RuleApiExtensions>,
	options?: Partial<ConfiguratorOptions>,
): CoreConfigurator<Root, RuleApiExtensions> {
	const apiExtensionFactories = {
		query,
	};
	return new CoreConfigurator(
		root,
		{ factory: ruleFactory, apiExtensionFactories },
		options,
	);
}

export function makeLocalizer(
	localization: Localization,
	locale: Locale,
): BoundLocalizer {
	return new Localizer(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		localization as any,
		locale,
	);
}

export function makeMetaRegistry(
	propertyMeta: PropertyMetaTree,
	resourceBaseUrl: string,
): BoundMetaRegistry {
	return new MetaRegistry(propertyMeta, resourceBaseUrl);
}

export function isProperty<TValue>(
	value: ConfigurationValue<Root, TValue>,
): value is Property<TValue>;
export function isProperty(value: unknown): value is Property;
export function isProperty<TValue = unknown>(
	value: unknown,
): value is Property<TValue> {
	return coreIsProperty(value);
}

export function findPropertyEntries<TEntry extends { property: Property }>(
	tree: Record<string, unknown>,
): TEntry[] {
	return Utility.findPropertyEntries(tree);
}

export function propertyDomainToFieldValues(
	domain: Domain<PropertyValue> | undefined,
): PropertyValue[] {
	if (domain === undefined) {
		return [];
	} else if (isArrayDomain(domain)) {
		return domain.filter(
			(value) => value !== undefined,
		) as unknown as PropertyValue[];
	} else if (isRangeDomain(domain)) {
		return [domain.min, domain.max];
	} else {
		throw new Error(`Unknown domain type for domain: ${domain}`);
	}
}

export function fieldValueToPropertyUpdate(
	value: PropertyValue,
): PropertyUpdate {
	if (Array.isArray(value)) {
		throw new Error(`Cannot convert array field value to property update`);
	}
	return value === null || value === undefined ? undefined : value;
}

export function fieldDomainToPropertyUpdate(
	domain: PropertyValue[],
): PropertyUpdate[] {
	return domain.map((value) => fieldValueToPropertyUpdate(value));
}

export function isUiProperty(property: Property): property is UiProperty {
	return Utility.isUiProperty(property, Definitions.Ui.ui);
}

export function assertIsLocalizable(
	property: Property,
): asserts property is UiProperty {
	return Utility.assertIsLocalizable(property, Definitions.Ui.ui);
}
